import { Platform } from 'react-native';

export type FunnelAuthMode = 'anonymous' | 'authenticated';
export type FunnelScenario = 'chat_to_delivery_no_close' | 'chat_to_form' | 'template_to_form' | 'unknown';
export type FunnelJourneyOutcome = 'completed' | 'abandoned' | 'redirected' | 'failed' | 'unknown';

export interface FunnelJourneyState {
    journeyId: string;
    journeySeq: number;
    startedAt: string;
    finishedAt: string | null;
    finishedOutcome: FunnelJourneyOutcome | null;
    entrySurface: string;
    entryPath: string;
    templateProjectId: string | null;
    authModeStart: FunnelAuthMode;
    authModeCurrent: FunnelAuthMode;
    chatClosedBeforeHandoff: boolean;
    formSeen: boolean;
    deliverySeen: boolean;
    scenarioCandidate: FunnelScenario;
    scenarioFinal: FunnelScenario | null;
    projectId: string | null;
    chatSessionId: string | null;
}

export interface StartJourneyInput {
    entrySurface?: string;
    entryPath?: string;
    templateProjectId?: string | null;
    authModeStart?: FunnelAuthMode;
    chatSessionId?: string | null;
    journeyId?: string;
    scenarioCandidate?: FunnelScenario;
    forceNew?: boolean;
}

interface JourneyResolution {
    state: FunnelJourneyState | null;
    resolvedScenario: FunnelScenario | null;
}

const JOURNEY_STORAGE_KEY = 'pz_funnel_journey_v2';
const JOURNEY_SEQ_STORAGE_KEY = 'pz_funnel_journey_seq_v2';

let cachedJourney: FunnelJourneyState | null = null;
let hasLoadedJourney = false;

function isWebRuntime(): boolean {
    return Platform.OS === 'web' && typeof window !== 'undefined';
}

function safeParseJSON<T>(rawValue: string | null): T | null {
    if (!rawValue) return null;

    try {
        return JSON.parse(rawValue) as T;
    } catch {
        return null;
    }
}

function inferEntryPath(): string {
    if (!isWebRuntime()) {
        return '/';
    }

    return `${window.location.pathname}${window.location.search}`;
}

function readJourneyFromStorage(): FunnelJourneyState | null {
    if (!isWebRuntime()) {
        return null;
    }

    return safeParseJSON<FunnelJourneyState>(window.sessionStorage.getItem(JOURNEY_STORAGE_KEY));
}

function writeJourneyToStorage(state: FunnelJourneyState | null): void {
    if (!isWebRuntime()) {
        return;
    }

    if (!state) {
        window.sessionStorage.removeItem(JOURNEY_STORAGE_KEY);
        return;
    }

    window.sessionStorage.setItem(JOURNEY_STORAGE_KEY, JSON.stringify(state));
}

function nextJourneySeq(): number {
    if (!isWebRuntime()) {
        return 1;
    }

    const current = Number(window.sessionStorage.getItem(JOURNEY_SEQ_STORAGE_KEY) || '0');
    const next = Number.isFinite(current) && current > 0 ? current + 1 : 1;
    window.sessionStorage.setItem(JOURNEY_SEQ_STORAGE_KEY, String(next));
    return next;
}

function getCachedJourney(): FunnelJourneyState | null {
    if (!hasLoadedJourney) {
        cachedJourney = readJourneyFromStorage();
        hasLoadedJourney = true;
    }

    return cachedJourney;
}

function setCachedJourney(state: FunnelJourneyState | null): void {
    cachedJourney = state;
    hasLoadedJourney = true;
    writeJourneyToStorage(state);
}

function generateJourneyId(): string {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    const template = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx';
    return template.replace(/[xy]/g, (char) => {
        const random = Math.floor(Math.random() * 16);
        const value = char === 'x' ? random : (random & 0x3) | 0x8;
        return value.toString(16);
    });
}

export function inferAuthModeFromStorage(): FunnelAuthMode {
    if (!isWebRuntime() || typeof window.localStorage === 'undefined') {
        return 'anonymous';
    }

    try {
        const candidateKeys = Object.keys(window.localStorage).filter((key) => key.includes('-auth-token'));

        for (const key of candidateKeys) {
            const parsed = safeParseJSON<any>(window.localStorage.getItem(key));
            if (!parsed) {
                continue;
            }

            const user = parsed?.user || parsed?.currentSession?.user || parsed?.session?.user;
            if (!user) {
                continue;
            }

            return user.email ? 'authenticated' : 'anonymous';
        }
    } catch {
        return 'anonymous';
    }

    return 'anonymous';
}

function normalizeScenarioCandidate(input: StartJourneyInput): FunnelScenario {
    if (input.scenarioCandidate) {
        return input.scenarioCandidate;
    }

    if (input.templateProjectId) {
        return 'template_to_form';
    }

    return 'chat_to_delivery_no_close';
}

export function getCurrentJourney(): FunnelJourneyState | null {
    return getCachedJourney();
}

export function getCurrentJourneyId(): string | null {
    return getCachedJourney()?.journeyId ?? null;
}

export function startJourney(input: StartJourneyInput = {}): FunnelJourneyState {
    const existing = getCachedJourney();

    if (existing && !existing.finishedAt && !input.forceNew) {
        const updated = {
            ...existing,
            ...(input.entrySurface ? { entrySurface: input.entrySurface } : {}),
            ...(input.entryPath ? { entryPath: input.entryPath } : {}),
            ...(input.templateProjectId ? { templateProjectId: input.templateProjectId } : {}),
            ...(input.chatSessionId ? { chatSessionId: input.chatSessionId } : {}),
            authModeCurrent: input.authModeStart || existing.authModeCurrent,
        };
        setCachedJourney(updated);
        return updated;
    }

    const state: FunnelJourneyState = {
        journeyId: input.journeyId || generateJourneyId(),
        journeySeq: nextJourneySeq(),
        startedAt: new Date().toISOString(),
        finishedAt: null,
        finishedOutcome: null,
        entrySurface: input.entrySurface || 'unknown',
        entryPath: input.entryPath || inferEntryPath(),
        templateProjectId: input.templateProjectId || null,
        authModeStart: input.authModeStart || inferAuthModeFromStorage(),
        authModeCurrent: input.authModeStart || inferAuthModeFromStorage(),
        chatClosedBeforeHandoff: false,
        formSeen: false,
        deliverySeen: false,
        scenarioCandidate: normalizeScenarioCandidate(input),
        scenarioFinal: null,
        projectId: null,
        chatSessionId: input.chatSessionId || null,
    };

    setCachedJourney(state);
    return state;
}

export function resumeJourney(journeyId: string, input: StartJourneyInput = {}): FunnelJourneyState {
    const existing = getCachedJourney();

    if (existing && existing.journeyId === journeyId) {
        return existing;
    }

    const resumed: FunnelJourneyState = {
        ...(existing || startJourney(input)),
        journeyId,
        entrySurface: input.entrySurface || existing?.entrySurface || 'unknown',
        entryPath: input.entryPath || existing?.entryPath || inferEntryPath(),
        templateProjectId: input.templateProjectId ?? existing?.templateProjectId ?? null,
        authModeStart: input.authModeStart || existing?.authModeStart || inferAuthModeFromStorage(),
        authModeCurrent: input.authModeStart || existing?.authModeCurrent || inferAuthModeFromStorage(),
        scenarioCandidate: input.scenarioCandidate || existing?.scenarioCandidate || normalizeScenarioCandidate(input),
        scenarioFinal: existing?.scenarioFinal || null,
        finishedAt: null,
        finishedOutcome: null,
        startedAt: existing?.startedAt || new Date().toISOString(),
    };

    setCachedJourney(resumed);
    return resumed;
}

export function updateJourney(patch: Partial<FunnelJourneyState>): FunnelJourneyState | null {
    const state = getCachedJourney();
    if (!state) {
        return null;
    }

    const updated: FunnelJourneyState = {
        ...state,
        ...patch,
    };

    setCachedJourney(updated);
    return updated;
}

export function resolveScenarioIfPossible(triggerEvent: string): JourneyResolution {
    const state = getCachedJourney();
    if (!state || state.scenarioFinal) {
        return { state, resolvedScenario: null };
    }

    let resolvedScenario: FunnelScenario | null = null;

    if (triggerEvent === 'form_opened') {
        resolvedScenario = state.templateProjectId ? 'template_to_form' : 'chat_to_form';
    }

    if (
        triggerEvent === 'delivery_opened'
        && !state.chatClosedBeforeHandoff
        && !state.formSeen
        && state.scenarioCandidate === 'chat_to_delivery_no_close'
    ) {
        resolvedScenario = 'chat_to_delivery_no_close';
    }

    if (!resolvedScenario) {
        return { state, resolvedScenario: null };
    }

    const updated = updateJourney({ scenarioFinal: resolvedScenario });
    return { state: updated, resolvedScenario };
}

export function finishJourney(outcome: FunnelJourneyOutcome): FunnelJourneyState | null {
    const state = getCachedJourney();
    if (!state) {
        return null;
    }

    if (state.finishedAt) {
        return null;
    }

    const updated = updateJourney({
        finishedAt: new Date().toISOString(),
        finishedOutcome: outcome,
        scenarioFinal: state.scenarioFinal || 'unknown',
    });

    return updated;
}

export function clearJourney(): void {
    setCachedJourney(null);
}
